import { forwardRef, useId, useRef, useEffect } from 'react'
import { TextFieldProps } from './props.js'

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      value,
      onChange,
      error,
      supportingText,
      multiline = false,
      rows = 3,
      leading,
      trailing,
      numeric = false,
      integer = false,
      bare = false,
      fullWidth = false,
      className = '',
      disabled,
      placeholder,
      type = 'text',
      ...attrs
    },
    ref
  ) => {
    const id = useId()
    const supportingId = useId()
    const inputRef = useRef<HTMLInputElement>(null)

    /*
     * `type="number"` accepts "e", "E", "+" and "-" because the HTML spec
     * allows exponent notation, so "1e5" is a legal value in a year field and
     * arrives as a number nobody typed. An integer field is therefore a TEXT
     * input with a numeric keypad, filtered to digits.
     */
    const inputType = integer ? 'text' : type
    const handleChange = (next: string) => {
      onChange(integer ? next.replace(/[^0-9]/g, '') : next)
    }
    const integerAttrs = integer
      ? ({ inputMode: 'numeric' as const, pattern: '[0-9]*', autoComplete: 'off' })
      : {}

    // Merge refs
    useEffect(() => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(inputRef.current)
        } else {
          ref.current = inputRef.current
        }
      }
    }, [ref])

    const isError = !!error

    if (bare) {
      // Bare mode: quiet but real field appearance with visible border; sits comfortably in a 48px row.
      const inputClasses = [
        // An in-table field must sit ABOVE its panel to read as editable.
        // bg-surface-low is DARKER than the surrounding panel, which made empty
        // cells look like static text on a new jobcard.
        'w-full h-8 px-3 rounded-sm bg-surface-high border border-outline outline-none',
        'text-on-surface placeholder:text-on-surface-subtle',
        'interactive hover:bg-surface-highest hover:border-on-surface-subtle',
        'focus:bg-surface-highest focus:border-on-surface focus:ring-1 focus:ring-on-surface',
        numeric && 'text-end tabular',
        disabled && 'opacity-38',
        className,
      ]
        .filter(Boolean)
        .join(' ')

      if (multiline) {
        return (
          <textarea
            ref={inputRef as any}
            aria-label={label}
            className={inputClasses}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            disabled={disabled}
            rows={rows}
            {...(attrs as any)}
          />
        )
      }

      return (
        <input
          ref={inputRef}
          type={inputType}
          {...integerAttrs}
          aria-label={label}
          className={inputClasses}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          {...attrs}
        />
      )
    }

    const containerClasses = [
      'flex flex-col gap-0',
      fullWidth && 'w-full',
      className,
    ]
      .filter(Boolean)
      .join(' ')

    const inputWrapperClasses = [
      'relative flex items-center',
      'bg-surface-low border border-outline-variant rounded-md',
      'interactive',
      isError && 'border-error',
      disabled && 'opacity-38',
    ]
      .filter(Boolean)
      .join(' ')

    const inputClasses = [
      'flex-1 min-w-0 bg-transparent outline-none px-3 py-2 h-9',
      'text-body-md text-on-surface placeholder:text-on-surface-subtle',
      numeric && 'text-end tabular',
    ]
      .filter(Boolean)
      .join(' ')

    const labelClasses = 'text-label-md text-on-surface-variant mb-1.5 block'

    const supportingClasses = [
      'text-body-sm mt-1.5',
      isError ? 'text-error' : 'text-on-surface-variant',
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <div className={containerClasses}>
        <label htmlFor={id} className={labelClasses}>
          {label}
        </label>
        <div className={inputWrapperClasses}>
          {leading && <div className="px-2 text-on-surface-variant flex-shrink-0">{leading}</div>}
          {multiline ? (
            <textarea
              ref={inputRef as any}
              id={id}
              className="flex-1 bg-transparent outline-none px-3 py-2 min-h-[80px] resize-none rounded-md text-body-md text-on-surface placeholder:text-on-surface-subtle"
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              disabled={disabled}
              rows={rows}
              placeholder={placeholder}
              aria-invalid={isError}
              aria-describedby={isError ? supportingId : undefined}
              {...(attrs as any)}
            />
          ) : (
            <input
              ref={inputRef}
              id={id}
              type={inputType}
              {...integerAttrs}
              className={inputClasses}
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              disabled={disabled}
              placeholder={placeholder}
              aria-invalid={isError}
              aria-describedby={isError ? supportingId : undefined}
              {...attrs}
            />
          )}
          {trailing && <div className="px-2 text-on-surface-variant flex-shrink-0">{trailing}</div>}
        </div>
        {(error || supportingText) && (
          <p
            id={supportingId}
            className={supportingClasses}
          >
            {error || supportingText}
          </p>
        )}
      </div>
    )
  }
)

TextField.displayName = 'TextField'
